import React, { useState, useEffect, useCallback } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Table } from 'reactstrap';
import { Translate, TextFormat, getSortState, translate } from 'react-jhipster';

import { getCountSchedulesByDay, getEntitiesByDate } from './scheduling.reducer';
import { IScheduling } from 'app/shared/model/scheduling.model';
import { Status } from 'app/shared/model/enumerations/scheduling-status.model';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT, AUTHORITIES, ColorScheme } from 'app/config/constants';
import { ASC, DESC, DEFAULT_ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  styled,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { Loading } from 'app/shared/util/loading';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { EditRounded } from '@mui/icons-material';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ArrowDownwardRounded, KeyboardArrowDown, KeyboardArrowUp, OpenInNewRounded } from '@mui/icons-material';
import { LocalizationProvider, PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { set } from 'lodash';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import { getEntities as getGasSupplies } from 'app/entities/gas-supply/gas-supply.reducer';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
interface ISchedulingDTO extends IScheduling {
  scheduling: IScheduling;
  companyId: number;
  userId: number;
  truckImplementId: number;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  truckDescription: string;
  licensePlate: string;
}

interface CustomTableRowProps {
  key: number;
  scheduling: ISchedulingDTO;
}

export const Scheduling = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const urlParams = new URLSearchParams(props.location.search);

  const countSchedules = useAppSelector(state => state.scheduling.entitiesAlt);
  const schedulingList = useAppSelector(state => state.scheduling.entities);
  const totalItems = useAppSelector(state => state.scheduling.totalItems);
  const loading = useAppSelector(state => state.scheduling.loading);

  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isTransporterManager = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_MANAGER]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const loggedUser = useAppSelector(state => state.authentication.account);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'id'), props.location.search)
  );
  const [currentMonth, setCurrentMonth] = useState<string | null>(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [searchLicensePlate, setSearchLicensePlate] = useState<string>(urlParams.get('licensePlate') || '');
  const [searchDescription, setSearchDescription] = useState<string>(urlParams.get('description') || '');
  const [searchUser, setSearchUser] = useState<string>(urlParams.get('userName') || '');
  const [schedulingId, setSchedulingId] = useState(urlParams.get('schedulingId') || '');
  const [loadedSchedules, setLoadedSchedules] = useState([{ day: '', length: 0 }]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [allList, setAllList] = useState(false);
  const [open, setOpen] = useState(false);

  const today = dayjs();
  const initialStartDate = today.format('YYYY-MM-DD');
  const initialEndDate = today.endOf('month').format('YYYY-MM-DD');

  const formattedDate = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null;

  const { match } = props;

  const headers = [
    { key: 'status', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.status" /> },
    { key: 'startTime', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.startTime" /> },
    { key: 'endTime', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.endTime" /> },
    { key: 'truckImplementPlate', sortable: false, component: <Translate contentKey="fleetSenseApp.scheduling.truckImplementPlate" /> },
    { key: 'truckImplementDescription', sortable: false, component: <Translate contentKey="fleetSenseApp.scheduling.truckImplementDescription" /> },
    { key: 'userFirstName', sortable: false, component: <Translate contentKey="fleetSenseApp.scheduling.user" /> },
    { key: 'description', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.id" /> },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntitiesByDate({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        licensePlate: searchLicensePlate,
        description: searchDescription,
        userName: searchUser,
        monthDate: currentMonth,
        startDate: formattedDate,
      })
    );
  }, [paginationState, selectedDate, formattedDate, allList]);

  useEffect(() => {
    CountSchedulingsByDay();
  }, []);

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [props.location.search]);

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    if (countSchedules && countSchedules.length > 0) {
      setLoadedSchedules(countSchedules);
    } else {
      setLoadedSchedules([{ day: '', length: 0 }]);
    }
  }, [countSchedules]);

  const CountSchedulingsByDay = useCallback(
    (startDate?: string, endDate?: string) => {
      dispatch(
        getCountSchedulesByDay({
          startDate: startDate ? startDate : initialStartDate,
          endDate: endDate ? endDate : initialEndDate,
        })
      );
    },
    [countSchedules]
  );

  const getSchedules = (newDate: Date) => {
    const firstDayOfMonth = dayjs(newDate).startOf('month').format('YYYY-MM-DD');
    setCurrentMonth(firstDayOfMonth);
    const formattedNewDate = dayjs(newDate).format('YYYY-MM-DD');
    const newEndDate = dayjs(formattedNewDate).endOf('month').format('YYYY-MM-DD');
    CountSchedulingsByDay(formattedNewDate, newEndDate);
  };

  const sortEntities = () => {
    const searchParams = [];
    if (searchLicensePlate !== '') {
      searchParams.push(`licensePlate=${encodeURIComponent(searchLicensePlate)}`);
    }
    if (schedulingId !== '') {
      searchParams.push(`schedulingId=${encodeURIComponent(schedulingId)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const handleSchedulingList = () => {
    setActivePage();
    setSelectedDate(null);
    setAllList(!allList);
  };

  const searchTypes: ISearchTypes[] = [
    {
      placeholder: translate('fleetSenseApp.scheduling.searchLicensePlate'),
      value: searchLicensePlate,
      setValue: setSearchLicensePlate,
    },
    {
      placeholder: translate('fleetSenseApp.scheduling.searchUser'),
      value: searchUser,
      setValue: setSearchUser,
    },
    {
      placeholder: translate('fleetSenseApp.scheduling.searchDescription'),
      value: searchDescription,
      setValue: setSearchDescription,
    },
  ];

  const statusChip = ({ status }: IScheduling) => (
    <Chip
      sx={{ width: '115px' }}
      label={status ? <Translate contentKey={`fleetSenseApp.SchedulingStatus.${status}`} /> : ''}
      color={status === Status.SCHEDULED ? 'warning' : status === Status.CANCELED ? 'error' : 'success'}
    />
  );
  const handleRowClick = id => {
    if (expandedId === id) {
      setOpen(!open);
      setExpandedId(open ? null : id);
    } else {
      setOpen(true);
      setExpandedId(id);
    }
  };
  const CustomTableRow = ({ key, scheduling }: CustomTableRowProps) => {
    return (
      <React.Fragment key={key}>
        <TableRow
          sx={{
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            backgroundColor: 'background.paper',
            filter: open && expandedId === scheduling?.id ? `drop-shadow(0 2px 2px ${theme.palette.grey[50]})` : 'none',
            borderTop: '4px solid',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
          }}
          onClick={() => handleRowClick(scheduling.id)}
        >
          <CustomHeadTableCell>{statusChip(scheduling)}</CustomHeadTableCell>
          <CustomHeadTableCell>
            {scheduling.startTime ? <TextFormat type="date" value={scheduling.startTime} format={APP_DATE_FORMAT} /> : null}
          </CustomHeadTableCell>
          <CustomHeadTableCell>
            {scheduling.endTime ? <TextFormat type="date" value={scheduling.endTime} format={APP_DATE_FORMAT} /> : null}
          </CustomHeadTableCell>
          <CustomHeadTableCell>
            {scheduling.licensePlate}
            <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${scheduling.truckImplementId}`}>
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </CustomHeadTableCell>
          <CustomHeadTableCell>{scheduling.truckDescription}</CustomHeadTableCell>
          <CustomHeadTableCell>
            {scheduling?.userFirstName} {scheduling?.userLastName}
            <IconButton color="secondary" component={Link} to={`admin/user-management?emailSearch=${encodeURIComponent(scheduling.userEmail)}`}>
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </CustomHeadTableCell>
          <CustomHeadTableCell>{scheduling.id}</CustomHeadTableCell>

          <CustomHeadTableCell>
            <IconButton aria-label="expand row" size="small" color="secondary">
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </CustomHeadTableCell>
        </TableRow>
        <TableRow
          sx={{
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            borderTop: '4px solid',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
          }}
        >
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse timeout="auto" unmountOnExit in={open && expandedId === scheduling.id}>
              <Box mb={5} mt={2} p={2} pt={0}>
                {summaryDetails(scheduling)}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const summaryDetails = ({ gasSupplies, description, type, createdBy, userEmail }: IScheduling) => {
    return loading ? (
      <Loading />
    ) : (
      <TableContainer sx={{ overflowY: 'hidden' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <CustomHeadTableCell>{translate('fleetSenseApp.scheduling.description')}</CustomHeadTableCell>
              <CustomHeadTableCell>{translate('fleetSenseApp.scheduling.type')}</CustomHeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: 'background.paper',
                borderTop: '4px solid',
                borderBottom: '4px solid',
                borderColor: 'primary.main',
              }}
            >
              <CustomHeadTableCell>{description}</CustomHeadTableCell>
              <CustomHeadTableCell>{translate(`fleetSenseApp.SchedulingTypes.${type}`)}</CustomHeadTableCell>
              {/*
            {gasSupplyList?.map((item, index) => (
              <TableRow key={item.id}>
                <CustomHeadTableCell>{item.distance}</CustomHeadTableCell>
                <CustomHeadTableCell>{item.price}</CustomHeadTableCell>
                <CustomHeadTableCell>{item.volume}</CustomHeadTableCell>
                 <CustomBodyTableCell sx={{ p: 0 }}>
                </CustomBodyTableCell>
                <CustomBodyTableCell>
                </CustomBodyTableCell>
                <CustomBodyTableCell>
                  <InspectionImageInput
                    disabled
                    description={item.observations}
                    image={item.image}
                    handleInputChange={null}
                    imageContentType="image/png"
                    index={index}
                  />
                </CustomBodyTableCell> 
              </TableRow>
            ))}
            */}

              {(userEmail === loggedUser.email || isAdmin || isManufacturerAdmin || isTransporterAdmin || isTransporterManager) && (
                <Box display={'flex'} alignSelf={'flex-end'}>
                  <IconButton
                    sx={{ alignSelf: 'flex-end' }}
                    component={Link}
                    to={`${match.url}/${expandedId}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                  >
                    <EditRounded color="secondary" fontSize="large" />
                  </IconButton>
                  <IconButton
                    component={Link}
                    to={`${match.url}/${expandedId}/cancel?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                  >
                    <BlockOutlinedIcon color="error" fontSize="large" />
                  </IconButton>
                  <IconButton component={Link} to={`gas-supplies/scheduling/${expandedId}`}>
                    <LocalGasStationOutlinedIcon color="secondary" fontSize="large" />
                  </IconButton>
                </Box>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
        <PageHeader
          headingId="scheuling-heading"
          headingDataCy="scheulingHeading"
          contentKey="fleetSenseApp.scheduling.home.title"
          buttonTextKey="fleetSenseApp.scheduling.home.createLabel"
          match={match}
        />
        <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column' }}>
          <Button
            onClick={handleSchedulingList}
            color="secondary"
            variant="contained"
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: theme.palette.secondary.main,
              borderRadius: '8px',
              textTransform: 'none',
              height: '42px',
              ml: { lg: '20px', xs: '0px' },
              marginBottom: '5px',
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'inherit' : 'secondary',
              },
            }}
          >
            <Translate contentKey={'fleetSenseApp.scheduling.viewAll'} />
          </Button>
          <Box display={'flex'} flexDirection={'row-reverse'} sx={{ flexDirection: { lg: 'row-reverse', xs: 'column' } }}>
            <Box sx={{ ml: { xs: '0px', lg: '40x', xl: '20px' }, display: 'flex', alignSelf: 'flex-start' }}>
              <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} showFilters={['filters']} />
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  width: { lg: '25vw ', md: '25vw', xs: '80vw' },
                  ml: { lg: '20px', xs: '0px' },
                  mr: '10px',
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Box
                  sx={{
                    minWidth: '280px',
                    '.css-epd502': {
                      width: 'fit-content',
                    },
                    '.MuiCalendarPicker-root': {
                      minWidth: '280px',
                    },
                    '.MuiDayPicker-header': {},
                    '.MuiPickersCalendarHeader-label': {
                      color: 'text.primary',
                    },
                    '.PrivatePickersYear-root': {
                      color: 'text.primary',
                    },
                    '.MuiDayPicker-weekContainer': {
                      mt: { lg: '10px', xs: '10px' },
                    },
                  }}
                >
                  <StaticDatePicker
                    showDaysOutsideCurrentMonth={false}
                    displayStaticWrapperAs="desktop"
                    value={selectedDate}
                    onChange={newValue => setSelectedDate(newValue)}
                    onMonthChange={newMonth => getSchedules(newMonth)}
                    renderInput={params => <TextField {...params} />}
                    renderDay={(day, selectedDates, DayComponentProps) => {
                      const formattedDay = dayjs(day).format('YYYY-MM-DD');
                      const scheduleForDay = loadedSchedules.find(scheduled => scheduled.day.startsWith(formattedDay));
                      const eventsCount = scheduleForDay ? scheduleForDay.length : 0;
                      return (
                        <Badge
                          badgeContent={eventsCount}
                          color="secondary"
                          sx={{
                            '& .MuiBadge-badge': {
                              right: '6px',
                              top: '3px',
                            },
                          }}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <PickersDay {...DayComponentProps} />
                        </Badge>
                      );
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>
        {!loading && (
          <TableContainer sx={{ mt: 1 }}>
            <Table aria-label="scheduling table">
              <TableHead>
                <TableRow>
                  {headers.map((header, i) => (
                    <TableCell
                      key={`entity-${i}`}
                      sx={{
                        borderBottom: 'none',
                        paddingBottom: 1,
                      }}
                    >
                      {header.sortable ? (
                        <TableSortLabel
                          active={paginationState.sort === header.key}
                          direction={paginationState.order as 'desc' | 'asc'}
                          onClick={sort(header.key)}
                          IconComponent={ArrowDownwardRounded}
                        >
                          <Typography
                            sx={{
                              color: 'text.primary',
                              whiteSpace: 'nowrap',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            {header.component}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        <Typography
                          sx={{
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            alignItems: 'center',
                          }}
                        >
                          {header.component}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ borderBottom: '0' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {schedulingList.map((scheduling: ISchedulingDTO, i) => (
                  <CustomTableRow key={i} scheduling={scheduling} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {loading && <Loading />}
        {schedulingList.length === 0 && !loading && <NoData title="Nenhum agendamento encontrado" />}
        <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={schedulingList} />
      </Box>
    </ThemeProvider>
  );
};

const CustomHeadTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  '&.MuiTableCell-root': {
    verticalAlign: 'middle',
  },
}));
